import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import CircularProgress from '@material-ui/core/CircularProgress'
import { X } from 'lucide-react'
import { RegularButton, RegularButtonOutline } from '../../atoms'
import i18n from '../../../assets/i18n'
import { tokens } from '../../../assets/tokens'
import * as S from './styled'

// El spinner ocupa el sitio del texto sin cambiar el ancho del botón
export const LoadingLabel = ({ loading, children }) => (
  <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    <span style={{ visibility: loading ? 'hidden' : 'visible' }}>{children}</span>
    {loading && <CircularProgress size={18} color="inherit" style={{ position: 'absolute' }} />}
  </span>
)

LoadingLabel.propTypes = {
  loading: PropTypes.bool,
  children: PropTypes.node
}

// Réplica de ActionDialog (headerVariant="default") del Storybook de Velora
// (hrbf_freezer/src/ui/components/ActionDialog.tsx): cabecera blanca con el título centrado,
// cierre arriba a la derecha y Cancelar + Confirmar centrados. El botón de confirmar va en el
// color del cliente, o en rojo con confirmColor="error" para acciones destructivas.
const ActionDialog = ({
  open,
  title,
  children,
  isPending,
  confirmDisabled,
  dismissible,
  onClose,
  onConfirm,
  cancelLabel,
  confirmLabel,
  confirmColor,
  confirmType,
  showActions,
  showCancel,
  maxWidth,
  fullWidth,
  disableBackdropClose,
  contentStyle,
  paperStyle,
  actions,
  className
}) => {
  // Si onConfirm devuelve una promesa (la petición), el botón muestra el spinner y el modal no se
  // puede cerrar hasta que termina: quien lo abre lo cierra cuando la petición ha ido bien
  const [isConfirming, setIsConfirming] = useState(false)
  const isMounted = useRef(true)
  useEffect(() => () => { isMounted.current = false }, [])
  const pending = isPending || isConfirming
  const hasContent = React.Children.toArray(children).length > 0

  const handleConfirm = event => {
    if (pending || !onConfirm) return
    const result = onConfirm(event)
    if (result && typeof result.then === 'function') {
      setIsConfirming(true)
      Promise.resolve(result)
        .catch(() => {})
        .finally(() => isMounted.current && setIsConfirming(false))
    }
  }

  const handleClose = (event, reason) => {
    if (pending || !dismissible) return
    if (disableBackdropClose && reason === 'backdropClick') return
    onClose && onClose(event, reason)
  }

  const confirmStyle = confirmColor === 'error' && !confirmDisabled && !pending ? { color: tokens.color.text.onDark } : undefined

  return (
    <Dialog
      open={!!open}
      onClose={handleClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      className={className}
      PaperProps={{ style: { ...S.paperStyle, ...paperStyle } }}
    >
      <DialogTitle disableTypography style={S.titleStyle}>
        {dismissible && (
          <IconButton size="small" onClick={onClose} disabled={pending} style={S.closeStyle} aria-label={cancelLabel || i18n.dialog.decline}>
            <X size={20} color={tokens.color.text.primary} />
          </IconButton>
        )}
        <S.Title>{title}</S.Title>
      </DialogTitle>

      {/* Sin cuerpo (solo la pregunta) no se pinta el contenido: su padding dejaba un hueco */}
      {hasContent && <DialogContent style={{ ...S.contentStyle, ...contentStyle }}>{children}</DialogContent>}

      {(showActions || actions) && (
        <DialogActions disableSpacing style={S.actionsStyle}>
          {actions || (
            <>
              {dismissible && showCancel && <RegularButtonOutline title={cancelLabel || i18n.dialog.decline} onClick={onClose} disabled={pending} type="button" />}
              <RegularButton
                title={<LoadingLabel loading={pending}>{confirmLabel || i18n.dialog.success}</LoadingLabel>}
                onClick={handleConfirm}
                disabled={confirmDisabled}
                color={confirmColor === 'error' ? tokens.color.error.main : undefined}
                style={confirmStyle}
                type={confirmType}
              />
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  )
}

ActionDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.node,
  children: PropTypes.node,
  isPending: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  /** Puede devolver una promesa: mientras no se resuelve, spinner en el botón y modal bloqueado */
  onConfirm: PropTypes.func,
  /** Si es false no se puede cerrar: sin X, sin Cancelar y sin cierre por fondo o Escape */
  dismissible: PropTypes.bool,
  onClose: PropTypes.func,
  cancelLabel: PropTypes.node,
  confirmLabel: PropTypes.node,
  confirmColor: PropTypes.oneOf(['default', 'error']),
  confirmType: PropTypes.string,
  showActions: PropTypes.bool,
  showCancel: PropTypes.bool,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
  fullWidth: PropTypes.bool,
  /** Para formularios: un clic despistado fuera no tira lo que se lleva escrito */
  disableBackdropClose: PropTypes.bool,
  contentStyle: PropTypes.object,
  paperStyle: PropTypes.object,
  /** Sustituye a Cancelar + Confirmar cuando el modal necesita otros botones */
  actions: PropTypes.node,
  className: PropTypes.string
}

ActionDialog.defaultProps = {
  isPending: false,
  confirmDisabled: false,
  dismissible: true,
  confirmColor: 'default',
  confirmType: 'button',
  showActions: true,
  showCancel: true,
  maxWidth: 'sm',
  fullWidth: true,
  disableBackdropClose: false
}

export default ActionDialog
